import { showAlert } from './tgDialogs';
import React, { useEffect, useMemo, useState } from 'react';
import { apiUrl } from './api';
import { payDigitalGoodWithStars } from './starsPay';
import './BoostShop.css';

type Props = {
  token: string;
  role: 'customer' | 'executor' | null;
  onBack: () => void;
  onPaid?: () => void;
};

type ShopItem = {
  sku: string;
  group: string;
  title: string;
  desc: string;
  stars: number | null;
  /** Выделить карточку (VIP) */
  accent?: boolean;
};

const num = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export default function BoostShop({ token, role, onBack, onPaid }: Props) {
  const [cfg, setCfg] = useState<any>(null);
  const [busySku, setBusySku] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/api/public/config'))
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setCfg(d);
      })
      .catch(() => {
        if (!cancelled) setCfg(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Что бесплатно для роли — показываем над каталогом. */
  const freeNote = useMemo(() => {
    if (!cfg) return '';
    if (role === 'executor') {
      const lim = num(cfg.max_responses_per_24h, 20);
      const vipLim = num(cfg.executor_vip_max_responses_per_24h, 50);
      return `Бесплатно: отклики — до ${lim} за 24 часа (с VIP — до ${vipLim}).`;
    }
    const priv = cfg.customer_privileges_stars || {};
    const lim = num(priv.daily_orders_limit, 5);
    const vipLim = num(priv.vip_daily_orders_limit, 10);
    return `Бесплатно: размещение заданий — до ${lim} в сутки (с VIP — до ${vipLim}).`;
  }, [cfg, role]);

  const packStars = (packs: any[] | undefined, sku: string, flat: unknown): number | null => {
    const fromArr = Array.isArray(packs)
      ? packs.find((p) => p && String(p.sku) === sku)?.stars
      : null;
    const n = Number(fromArr ?? flat);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const items: ShopItem[] = useMemo(() => {
    if (!cfg) return [];
    if (role === 'executor') {
      const tops = Array.isArray(cfg.executor_top_tiers) ? cfg.executor_top_tiers : [];
      const channelPacks = cfg.executor_service_channel_packs;
      const postingPacks = cfg.executor_service_posting_packs;
      const commission = num(cfg.executor_vip_commission_percent, 7);
      const vipResponses = num(cfg.executor_vip_max_responses_per_24h, 50);
      const vipServicePosts = num(
        cfg.executor_vip_service_channel_posts ?? cfg.executor_vip_service_channel_posts_per_period,
        10
      );
      return [
        {
          sku: 'executor_vip',
          group: 'VIP',
          accent: true,
          title: 'VIP исполнителя · 30 дн.',
          desc:
            `Комиссия ${commission}%, буст в ТОП, 3 поста анкеты и ${vipServicePosts} постов услуг в канал, ` +
            `до ${vipResponses} откликов в сутки и рассылка заданий на весь период.`,
          stars: cfg.executor_vip_month_stars ?? null
        },
        ...tops.map((t: any) => ({
          sku: `executor_top_${t.id}`,
          group: 'ТОП профиля',
          title: `ТОП ${t.days} дн.`,
          desc: 'Ваша анкета выше в ленте заказчиков.',
          stars: t.stars ?? null
        })),
        {
          sku: 'service_channel_1',
          group: 'Посты в канал',
          title: 'Пост в канал ×1',
          desc: 'Публикация анкеты или услуги в Telegram-канале биржи.',
          stars: packStars(channelPacks, 'service_channel_1', cfg.service_channel_pack_1_price_stars)
        },
        {
          sku: 'service_channel_5',
          group: 'Посты в канал',
          title: 'Посты в канал ×5',
          desc: 'Для анкеты или любых ваших услуг.',
          stars: packStars(channelPacks, 'service_channel_5', cfg.service_channel_pack_5_price_stars)
        },
        {
          sku: 'service_channel_10',
          group: 'Посты в канал',
          title: 'Посты в канал ×10',
          desc: 'Для анкеты или любых ваших услуг.',
          stars: packStars(channelPacks, 'service_channel_10', cfg.service_channel_pack_10_price_stars)
        },
        {
          sku: 'category_alerts_10d',
          group: 'Рассылка заданий',
          title: 'Рассылка заданий · 10 дн.',
          desc: 'Новые задания в выбранных категориях сразу в боте.',
          stars: cfg.category_alerts_10d_stars ?? null
        },
        {
          sku: 'category_alerts_30d',
          group: 'Рассылка заданий',
          title: 'Рассылка заданий · 30 дн.',
          desc: 'Новые задания в выбранных категориях сразу в боте.',
          stars: cfg.category_alerts_30d_stars ?? null
        },
        {
          sku: 'service_posting_1',
          group: 'Размещение услуг',
          title: 'Размещение услуги ×1',
          desc: 'Слот публикации услуги в каталоге.',
          stars: packStars(postingPacks, 'service_posting_1', cfg.service_posting_pack_1_price_stars)
        },
        {
          sku: 'service_posting_5',
          group: 'Размещение услуг',
          title: 'Размещения услуг ×5',
          desc: 'Пакет слотов публикации услуг.',
          stars: packStars(postingPacks, 'service_posting_5', cfg.service_posting_pack_5_price_stars)
        },
        {
          sku: 'service_posting_10',
          group: 'Размещение услуг',
          title: 'Размещения услуг ×10',
          desc: 'Пакет слотов публикации услуг.',
          stars: packStars(postingPacks, 'service_posting_10', cfg.service_posting_pack_10_price_stars)
        }
      ];
    }

    const priv = cfg.customer_privileges_stars || {};
    const topPacks = priv.order_top_packs;
    const urgentPacks = priv.urgent_packs;
    const topHours = num(priv.order_top_hours ?? cfg.order_top_hours, 24);
    const vipDaily = num(priv.vip_daily_orders_limit, 10);
    const vipTop = num(priv.vip_order_top_slots, 10);
    const vipUrgent = num(priv.vip_urgent_slots, 3);
    const vipChannel = num(priv.vip_channel_posts, 3);
    return [
      {
        sku: 'customer_vip',
        group: 'VIP',
        accent: true,
        title: 'VIP заказчика · 30 дн.',
        desc:
          `До ${vipDaily} заданий в сутки, ${vipTop} слотов «ТОП задания», ${vipUrgent} «Срочно» ` +
          `и ${vipChannel} поста задания в канал.`,
        stars: priv.vip_month_stars ?? cfg.customer_vip_month_stars ?? null
      },
      {
        sku: 'order_top_1',
        group: 'ТОП задания',
        title: 'ТОП задания ×1',
        desc: `Задание закреплено вверху ленты на ${topHours} ч.`,
        stars: packStars(topPacks, 'order_top_1', cfg.order_top_pack_1_price_stars)
      },
      {
        sku: 'order_top_5',
        group: 'ТОП задания',
        title: 'ТОП задания ×5',
        desc: `Пять слотов, каждый — ${topHours} ч вверху ленты.`,
        stars: packStars(topPacks, 'order_top_5', cfg.order_top_pack_5_price_stars)
      },
      {
        sku: 'order_top_10',
        group: 'ТОП задания',
        title: 'ТОП задания ×10',
        desc: `Десять слотов, каждый — ${topHours} ч вверху ленты.`,
        stars: packStars(topPacks, 'order_top_10', cfg.order_top_pack_10_price_stars)
      },
      {
        sku: 'urgent_1',
        group: 'Срочно',
        title: 'Срочно ×1',
        desc: 'Метка 🔥 «Срочно» — исполнители видят задание первым делом.',
        stars: packStars(urgentPacks, 'urgent_1', cfg.urgent_pack_1_price_stars)
      },
      {
        sku: 'urgent_3',
        group: 'Срочно',
        title: 'Срочно ×3',
        desc: 'Три метки «Срочно» на задания.',
        stars: packStars(urgentPacks, 'urgent_3', cfg.urgent_pack_3_price_stars)
      },
      {
        sku: 'order_channel_post',
        group: 'Пост в канал',
        title: 'Пост задания в канал',
        desc: 'Публикация открытого задания в Telegram-канале биржи.',
        stars: priv.order_channel_post?.stars ?? cfg.order_channel_post_stars ?? null
      }
    ];
  }, [cfg, role]);

  const buy = async (sku: string) => {
    if (!token || busySku) return;
    setBusySku(sku);
    try {
      const status = await payDigitalGoodWithStars(token, sku);
      if (status === 'paid') {
        showAlert('Оплата прошла. Привилегия скоро появится в профиле.');
        onPaid?.();
      } else if (status === 'cancelled') {
        /* ignore */
      } else {
        showAlert('Оплата Stars не завершена. Попробуйте ещё раз.');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Ошибка оплаты Stars');
    } finally {
      setBusySku(null);
    }
  };

  return (
    <div className="App">
      <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: 12 }}>
        <div className="boost-shop">
          <div className="boost-shop__top">
            <button type="button" className="boost-shop__back" onClick={onBack} aria-label="Назад">
              ←
            </button>
            <div>
              <h2 className="boost-shop__title">Буст</h2>
              <p className="boost-shop__lead">
                {role === 'executor'
                  ? 'Покупки для исполнителя за Telegram Stars'
                  : 'Покупки для заказчика за Telegram Stars'}
              </p>
            </div>
          </div>

          <div className="boost-shop__hero" aria-hidden>
            <span className="boost-shop__star">⭐</span>
            <p>Продвижение, VIP и посты в канал — оплата только Stars</p>
          </div>

          {freeNote ? <p className="boost-shop__free">{freeNote}</p> : null}

          {!cfg ? (
            <p className="boost-shop__muted">Загрузка каталога…</p>
          ) : items.length === 0 ? (
            <p className="boost-shop__muted">Нет доступных товаров для вашей роли.</p>
          ) : (
            <div className="boost-shop__list">
              {items.map((it, i) => (
                <React.Fragment key={it.sku}>
                  {i === 0 || items[i - 1].group !== it.group ? (
                    <h4 className="boost-shop__group">{it.group}</h4>
                  ) : null}
                  <article
                    className={`boost-shop__card${it.accent ? ' boost-shop__card--accent' : ''}`}
                  >
                    <div className="boost-shop__card-text">
                      <h3>{it.title}</h3>
                      <p>{it.desc}</p>
                    </div>
                    <button
                      type="button"
                      className="boost-shop__buy"
                      disabled={busySku != null || it.stars == null}
                      onClick={() => void buy(it.sku)}
                    >
                      {busySku === it.sku
                        ? '…'
                        : it.stars != null
                          ? `${it.stars} ⭐`
                          : '—'}
                    </button>
                  </article>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </header>
    </div>
  );
}
